import { useEffect, useState } from "react";
import { MapPin, Trash2 } from "lucide-react";

import { Combobox, type ComboOption } from "@/components/cinch/Combobox";
import { Field } from "@/components/cinch/Field";
import { DatePicker, fromIsoDate, toIsoDate } from "@/components/cinch/DatePicker";
import { IconButton } from "@/components/cinch/IconButton";
import { NumberInput } from "@/components/cinch/NumberInput";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

import {
  fmtDate,
  useCompanySearch,
  useCreateRequest,
  useDepartments,
  useDuplicateCheck,
  useShipToSuggestions,
  useSummary,
  type RecipientKind,
  type RequestLineIn,
} from "../api";

interface DraftLine extends RequestLineIn {
  key: string;
}

function blankLine(): DraftLine {
  return {
    key: Math.random().toString(36).slice(2),
    item_id: "",
    quantity: 1,
    recipient_kind: "person",
    recipient_name: "",
    recipient_company_id: null,
    recipient_company_name: "",
    occasion: "",
    ship_to_name: "",
    ship_to_address: "",
    ship_to_phone: "",
  };
}

/**
 * "Request swag" — one line per recipient, because the duplicate-gift check is
 * per person. Asking for four blankets as quantity 4 would give the check
 * nothing to work with, so the form pushes you toward naming each one.
 */
export function RequestDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const summary = useSummary();
  const departments = useDepartments();
  const create = useCreateRequest();

  const [departmentId, setDepartmentId] = useState<string>("");
  const [purpose, setPurpose] = useState("");
  const [needsShipping, setNeedsShipping] = useState(false);
  const [neededBy, setNeededBy] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([blankLine()]);

  useEffect(() => {
    if (!open) {
      setLines([blankLine()]);
      setPurpose("");
      setNeedsShipping(false);
      setNeededBy("");
      create.reset();
    }
    // create.reset is stable enough for this; re-running on every render of the
    // mutation object would clear the error the user is meant to read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const itemOptions: ComboOption[] = (summary.data?.stock ?? [])
    .filter((s) => s.free > 0)
    .map((s) => ({
      id: s.item_id,
      label: s.item_name,
      subtitle: `${s.free} free of ${s.on_hand} on hand`,
      badge: String(s.free),
    }));

  const update = (key: string, patch: Partial<DraftLine>) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const totalByItem = new Map<string, number>();
  for (const line of lines) {
    if (line.item_id) {
      totalByItem.set(line.item_id, (totalByItem.get(line.item_id) ?? 0) + line.quantity);
    }
  }
  const overAsk = (summary.data?.stock ?? []).filter(
    (s) => (totalByItem.get(s.item_id) ?? 0) > s.free,
  );

  const ready =
    lines.length > 0 &&
    lines.every((l) => l.item_id && l.quantity > 0) &&
    overAsk.length === 0;

  const submit = () => {
    create.mutate(
      {
        department_id: departmentId || null,
        purpose: purpose || null,
        needs_shipping: needsShipping,
        needed_by: neededBy || null,
        lines: lines.map(({ key: _key, ...line }) => ({
          ...line,
          recipient_name: line.recipient_name || null,
          recipient_company_name: line.recipient_company_name || null,
          occasion: line.occasion || null,
          ship_to_name: line.ship_to_name || null,
          ship_to_address: line.ship_to_address || null,
        })),
      },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Request swag</DialogTitle>
          <DialogDescription>
            An admin approves before anything leaves the shelf. Naming each
            recipient is what lets us warn you if they’ve already had one.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {/* items-end: "Expense to department" carries its label above the
              control while DatePicker floats its own inside, so without this
              the two inputs sit at different heights. */}
          <div className="grid items-end gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="swag-dept">Expense to department</Label>
              <Select value={departmentId} onValueChange={setDepartmentId}>
                <SelectTrigger id="swag-dept">
                  <SelectValue placeholder="Pick a department" />
                </SelectTrigger>
                <SelectContent>
                  {(departments.data ?? []).map((dept) => (
                    <SelectItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DatePicker
              id="swag-needed-by"
              value={fromIsoDate(neededBy)}
              onChange={(d) => setNeededBy(d ? toIsoDate(d) : "")}
              aria-label="Needed by"
            />
          </div>

          <Textarea
            placeholder="What's this for? (renewal thank-you, conference booth, exec gift…)"
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            rows={2}
          />

          <div className="flex items-center gap-2">
            <Switch
              id="swag-shipping"
              checked={needsShipping}
              onCheckedChange={setNeedsShipping}
            />
            <Label htmlFor="swag-shipping" className="text-sm font-normal">
              Needs shipping — Adrey will send it and record the tracking
            </Label>
          </div>

          <div className="flex flex-col gap-4">
            {lines.map((line, index) => (
              <LineEditor
                key={line.key}
                line={line}
                index={index}
                itemOptions={itemOptions}
                needsShipping={needsShipping}
                onChange={(patch) => update(line.key, patch)}
                onRemove={
                  lines.length > 1
                    ? () => setLines((prev) => prev.filter((l) => l.key !== line.key))
                    : undefined
                }
              />
            ))}
            <Button
              variant="outline"
              size="sm"
              className="self-start"
              onClick={() => setLines((prev) => [...prev, blankLine()])}
            >
              Add another recipient
            </Button>
          </div>

          {overAsk.length > 0 && (
            <Alert variant="warning">
              Not enough free stock:{" "}
              {overAsk
                .map((s) => `${s.item_name} (${s.free} free, ${totalByItem.get(s.item_id)} asked)`)
                .join("; ")}
            </Alert>
          )}
          {create.error && <Alert variant="danger">{String(create.error)}</Alert>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!ready || create.isPending} onClick={submit}>
            {create.isPending ? "Submitting…" : "Submit request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LineEditor({
  line,
  index,
  itemOptions,
  needsShipping,
  onChange,
  onRemove,
}: {
  line: DraftLine;
  index: number;
  itemOptions: ComboOption[];
  needsShipping: boolean;
  onChange: (patch: Partial<DraftLine>) => void;
  onRemove?: () => void;
}) {
  const [companyQuery, setCompanyQuery] = useState("");
  const companies = useCompanySearch(companyQuery);

  // Debounce the duplicate check so it fires on a settled name, not per keystroke.
  const [debouncedName, setDebouncedName] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedName(line.recipient_name ?? ""), 400);
    return () => clearTimeout(timer);
  }, [line.recipient_name]);

  const dupes = useDuplicateCheck(
    {
      recipient_name: debouncedName || undefined,
      company_id: line.recipient_company_id ?? undefined,
      company_name: line.recipient_company_name ?? undefined,
      item_id: line.item_id || undefined,
    },
    line.recipient_kind === "person" && debouncedName.trim().length >= 3,
  );

  // Only worth asking once shipping is actually on — and a company alone is
  // enough, since "somewhere at NAPA" is a useful answer even before a name.
  const shipTo = useShipToSuggestions(
    {
      recipient_name: debouncedName || undefined,
      company_id: line.recipient_company_id ?? undefined,
      company_name: line.recipient_company_name ?? undefined,
    },
    needsShipping &&
      (debouncedName.trim().length >= 3 ||
        Boolean(line.recipient_company_id || line.recipient_company_name)),
  );
  const suggestions = shipTo.data?.suggestions ?? [];

  const companyOptions: ComboOption[] = (companies.data?.items ?? []).map((c) => ({
    id: c.id,
    label: c.name,
    subtitle: c.account_name ?? undefined,
  }));

  return (
    <div className="flex flex-col gap-3 rounded-xl border p-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Recipient {index + 1}
        </span>
        {onRemove && (
          <IconButton
            title={`Remove recipient ${index + 1}`}
            onClick={onRemove}
            hoverClassName="hover:bg-danger-light hover:text-danger"
          >
            <Trash2 className="h-4 w-4" />
          </IconButton>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="flex flex-col gap-1.5 sm:col-span-2">
          <Label>Item</Label>
          <Combobox
            options={itemOptions}
            value={line.item_id || null}
            onChange={(id) => onChange({ item_id: id ?? "" })}
            placeholder="Pick an item"
            emptyText="Nothing free to request."
          />
        </div>
        <NumberInput
          id={`qty-${line.key}`}
          label="Quantity"
          maxDecimals={0}
          value={line.quantity}
          onChange={(q) => onChange({ quantity: Math.max(q ?? 1, 1) })}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label>Who's it for?</Label>
        <Select
          value={line.recipient_kind}
          onValueChange={(v) => onChange({ recipient_kind: v as RecipientKind })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="person">A named person</SelectItem>
            <SelectItem value="group">A raffle / booth giveaway</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {line.recipient_kind === "person" ? (
        // items-end for the same reason as the department row above: Field
        // floats its label, Company puts one above the Combobox.
        <div className="grid items-end gap-3 sm:grid-cols-2">
          <Field
            id={`name-${line.key}`}
            label="Recipient name"
            value={line.recipient_name ?? ""}
            onChange={(e) => onChange({ recipient_name: e.target.value })}
          />
          <div className="flex flex-col gap-1.5">
            <Label>Company</Label>
            <Combobox
              options={companyOptions}
              value={line.recipient_company_id ?? null}
              onChange={(id) => {
                const found = companies.data?.items.find((c) => c.id === id);
                onChange({
                  recipient_company_id: id,
                  recipient_company_name: found?.name ?? line.recipient_company_name ?? null,
                });
              }}
              onSearchChange={setCompanyQuery}
              loading={companies.isFetching}
              placeholder="Search Cinch companies…"
              searchPlaceholder="Type at least 2 letters…"
              emptyText="No match — put the company in the notes instead."
            />
          </div>
        </div>
      ) : (
        <Field
          id={`occasion-${line.key}`}
          label="Occasion (Christmas raffle, SEMA booth…)"
          value={line.occasion ?? ""}
          onChange={(e) => onChange({ occasion: e.target.value })}
        />
      )}

      {needsShipping && (
        <div className="flex flex-col gap-3">
          {suggestions.length > 0 && (
            <div className="flex flex-col gap-1.5 rounded-xl border border-dashed p-3">
              <p className="text-xs font-medium text-muted-foreground">
                We&apos;ve posted here before
              </p>
              <div className="flex flex-wrap gap-2">
                {suggestions.map((sug) => (
                  <button
                    key={`${sug.matched_on}-${sug.ship_to_address}`}
                    type="button"
                    onClick={() =>
                      onChange({
                        // The person's own name goes on the label, not
                        // whoever the last box was addressed to.
                        ship_to_name:
                          sug.matched_on === "person"
                            ? (sug.ship_to_name ?? line.recipient_name ?? "")
                            : (line.recipient_name ?? ""),
                        ship_to_address: sug.ship_to_address,
                        ship_to_phone: sug.ship_to_phone ?? line.ship_to_phone ?? "",
                      })
                    }
                    className="flex max-w-full flex-col items-start gap-0.5 rounded-lg border px-2.5 py-1.5 text-left text-xs hover:border-brand hover:bg-brand-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <span className="flex items-center gap-1.5">
                      <MapPin className="h-3 w-3 shrink-0 text-muted-foreground" />
                      <span className="font-medium">{sug.ship_to_address}</span>
                    </span>
                    <span className="text-muted-foreground">
                      {sug.matched_on === "person"
                        ? "Where their last one went"
                        : `Sent to ${sug.last_recipient ?? "a colleague"} here`}
                      {sug.last_used ? ` · ${fmtDate(sug.last_used)}` : ""}
                      {sug.times_used > 1 ? ` · ${sug.times_used} boxes` : ""}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              id={`shipname-${line.key}`}
              label="Ship to name"
              value={line.ship_to_name ?? ""}
              onChange={(e) => onChange({ ship_to_name: e.target.value })}
            />
            <Field
              id={`shipphone-${line.key}`}
              label="Phone number"
              type="tel"
              value={line.ship_to_phone ?? ""}
              onChange={(e) => onChange({ ship_to_phone: e.target.value })}
              helper="Carriers ask for one on delivery"
            />
          </div>
          <Field
            id={`shipaddr-${line.key}`}
            label="Ship to address"
            value={line.ship_to_address ?? ""}
            onChange={(e) => onChange({ ship_to_address: e.target.value })}
          />
        </div>
      )}

      {dupes.data && (dupes.data.person_matches.length > 0 || dupes.data.company_matches.length > 0) && (
        <Alert variant={dupes.data.same_item_same_person ? "danger" : "warning"}>
          <div className="flex flex-col gap-1">
            {dupes.data.same_item_same_person && (
              <strong>
                {line.recipient_name} has already had this exact item.
              </strong>
            )}
            {dupes.data.person_matches.length > 0 && (
              <span>
                Already given to {line.recipient_name}:{" "}
                {dupes.data.person_matches
                  .map((m) => `${m.item_name} (${fmtDate(m.use_date)})`)
                  .join(", ")}
              </span>
            )}
            {dupes.data.company_matches.length > 0 && (
              <span className="text-xs">
                Others at this company have had:{" "}
                {dupes.data.company_matches
                  .slice(0, 5)
                  .map((m) => `${m.item_name} → ${m.recipient_name ?? "—"}`)
                  .join(", ")}
                {dupes.data.company_matches.length > 5 && (
                  <Badge variant="neutral" className="ml-1">
                    +{dupes.data.company_matches.length - 5} more
                  </Badge>
                )}
              </span>
            )}
          </div>
        </Alert>
      )}
    </div>
  );
}
